import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import { FlagIndicatorDisplay } from "@/components/interactions/FlagIndicatorDisplay";
import { highlightSubstringMatches } from "@/lib/highlight-keywords";
import { buildNoteExcerpt } from "@/lib/note-excerpt";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Flag } from "lucide-react";
import type { Interaction } from "@/pages/interactions";

function formatEventType(eventType: string): string {
  return eventType === "BEH_ALERT" ? "Alert" : "Observation";
}

function formatOccurredAt(dateStr: string): string {
  const date = new Date(dateStr.endsWith("Z") ? dateStr : dateStr + "Z");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  const hours = date.getUTCHours();
  const mins = date.getUTCMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${month} ${day}, ${year} ${hour12}:${mins} ${ampm}`;
}

interface InteractionHoverCardProps {
  interaction: Interaction;
  zoneLabel: string;
  searchQuery?: string;
  onOpenDrawer: () => void;
  onViewAnimal: () => void;
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function InteractionHoverCard({
  interaction,
  zoneLabel,
  searchQuery,
  onOpenDrawer,
  onViewAnimal,
  children,
  open,
  onOpenChange,
}: InteractionHoverCardProps) {
  const isHighlighted = interaction.flagReasons?.some((r) => r.type === "highlighted") ?? false;
  const hasRenderableFlags =
    interaction.flagReasons?.some((r) => r.type === "long_note" || r.type === "keyword") ?? false;

  const notePreview = (): React.ReactNode => {
    const raw = interaction.rawNote;
    if (!raw) return <span className="italic">No note</span>;
    if (searchQuery?.trim()) {
      const excerpt = buildNoteExcerpt(raw, searchQuery.trim(), 125);
      if (excerpt) {
        return <>{highlightSubstringMatches(excerpt, searchQuery.trim())}</>;
      }
    }
    return (
      <>
        {raw.slice(0, 250)}
        {raw.length > 250 ? "…" : ""}
      </>
    );
  };

  return (
    <HoverCard
      openDelay={500}
      closeDelay={150}
      open={open}
      onOpenChange={onOpenChange}
    >
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        className="w-80 p-0 max-h-[var(--radix-hover-card-content-available-height)] overflow-y-auto"
        align="start"
        collisionPadding={{ top: 8 }}
        data-testid={`hover-card-interaction-${interaction.id}`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b">
          <AnimalAvatar
            animalId={interaction.animalId}
            photoUrl={interaction.animalPhotoUrl ?? null}
            name={interaction.animalName ?? interaction.animalId}
            species={interaction.animalSpecies ?? ""}
            className="shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-sm truncate">
                {interaction.animalName ?? interaction.animalId}
              </span>
              {isHighlighted && (
                <Flag
                  className="h-3 w-3 fill-amber-500 text-amber-500 shrink-0"
                  aria-label="Highlighted"
                />
              )}
              <Badge variant="outline" className="text-xs ml-auto shrink-0">
                {interaction.locationCode}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">{interaction.animalId}</div>
            {interaction.animalSpecies && (
              <div className="text-xs text-muted-foreground">
                {interaction.animalSpecies.toUpperCase()}
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="p-4 text-sm">
          {/* Note — prominent, first field */}
          <div className="pb-3 mb-3 border-b">
            <span className="text-xs text-muted-foreground block mb-1.5">Note</span>
            <blockquote className="border-l-2 border-muted-foreground/40 pl-3 italic text-sm text-foreground line-clamp-6">
              {notePreview()}
            </blockquote>
          </div>

          <div className="space-y-1 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground shrink-0">Date</span>
            <span className="text-right">{formatOccurredAt(interaction.occurredAt)}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground shrink-0">Score</span>
            <span className="text-right">
              {interaction.scoreCode ?? "—"}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground shrink-0">Author</span>
            <span className="text-right">{interaction.author ?? "—"}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground shrink-0">Type</span>
            <Badge variant="outline" className="text-xs">
              {formatEventType(interaction.eventType)}
            </Badge>
          </div>
          <div className="flex items-start justify-between gap-2">
            <span className="text-muted-foreground shrink-0">Flags</span>
            <div className="text-right">
              {hasRenderableFlags ? (
                <FlagIndicatorDisplay flagReasons={interaction.flagReasons} />
              ) : (
                <span className="text-muted-foreground" aria-label="No flags">
                  —
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground shrink-0">Kennel</span>
            <span className="text-right">{zoneLabel}</span>
          </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex gap-2 p-4 pt-0">
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDrawer();
            }}
            data-testid={`button-hover-card-view-interaction-${interaction.id}`}
          >
            View Interaction
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={(e) => {
              e.stopPropagation();
              onViewAnimal();
            }}
            data-testid={`button-hover-card-view-animal-${interaction.id}`}
          >
            View Animal
          </Button>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
